// crm-frontend/src/components/Operations/Inventory/ProductsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useInventoryApi from "./useInventoryApi";
import WidgetPanel from "../../ui/WidgetPanel";
import { num } from "../../Analytics/metricValue";
import "../OpsShared.css";
import "../../Analytics/pages/AnalyticsShared.css";
import "../../../styles/data-table.css";
import "./Inventory.css";

const TYPES = [
  ["all", "All types"], ["chemical", "Chemical"], ["consumable", "Consumable"],
  ["equipment", "Equipment"], ["part", "Part"],
];

const qtyCell = (v) => {
  const n = num(v);
  return n === null ? "—" : n.toLocaleString();
};

export default function ProductsPage() {
  const { listProducts, deleteProduct } = useInventoryApi();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await listProducts();
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    return rows.filter((p) => {
      const pType = p.type || "other";
      if (type !== "all" && pType !== type) return false;
      if (q) {
        const vendorLabel = p.vendor_name || p.vendor?.name || "";
        const blob = `${p.sku} ${p.name} ${vendorLabel} ${p.upc}`.toLowerCase();
        if (!blob.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, q, type]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await deleteProduct(id);
    await load();
  };

  return (
    <div className="ops-page">
      <header className="ops-head">
        <div>
          <h1>Products</h1>
          <div className="ops-muted">Everything you stock — chemicals, consumables, equipment and parts.</div>
        </div>
        <div className="ops-actions">
          <input className="ops-search" placeholder="Search SKU, name, vendor…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="ops-btn" onClick={() => navigate("new")}>+ New product</button>
          <button className="ops-btn secondary" onClick={() => navigate("new?mode=scan")}>+ From label</button>
        </div>
      </header>

      <div className="analytics-chip-row" role="tablist" aria-label="Type">
        {TYPES.map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`analytics-chip analytics-chip--button ${type === k ? "is-active" : ""}`}
            onClick={() => setType(k)}
          >
            {l}
          </button>
        ))}
      </div>

      <WidgetPanel className="gt-table-shell" padded={false}>
        <div className="gt-table-scroll">
          <div className="gt-table-wrapper">
            <table className="gt-table">
              <thead>
                <tr>
                  <th>SKU</th><th>Name</th><th>Type</th><th>UoM</th>
                  <th>Vendor</th><th className="num">Reorder min</th><th className="num">Target</th><th>Flags</th><th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={9}><div className="gt-empty">Loading…</div></td></tr>
                ) : filtered.length === 0 ? (
                  <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={9}><div className="gt-empty">No products match. Click “New product” or “From label”.</div></td></tr>
                ) : filtered.map((p) => (
                  <tr key={p.id} className="gt-row" onClick={() => navigate(`${p.id}/edit`)}>
                    <td className="gt-cell mono">{p.sku || "—"}</td>
                    <td className="gt-cell name-col">{p.name || "—"}</td>
                    <td className="gt-cell cap">{p.type || "other"}</td>
                    <td className="gt-cell">{p.uom || "—"}</td>
                    <td className="gt-cell">{p.vendor_name || p.vendor?.name || "—"}</td>
                    <td className="gt-cell num">{qtyCell(p.reorder_min)}</td>
                    <td className="gt-cell num">{qtyCell(p.reorder_target)}</td>
                    <td className="gt-cell gt-cell--muted">
                      {[p.is_chemical ? "chemical" : null, p.requires_lot ? "lot" : null, p.requires_expiry ? "expiry" : null].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="gt-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="gt-actions">
                        <Link to={`${p.id}/edit`}>Edit</Link>
                        <button type="button" className="gt-link danger" onClick={() => onDelete(p.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="gt-table-footer">
          <div className="gt-table-count">Showing <strong>{filtered.length}</strong> of <strong>{rows.length}</strong> products</div>
        </div>
      </WidgetPanel>
    </div>
  );
}
